import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, MARKET_META } from "../lib/api";
import { AdminShell } from "./AdminShell";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const todayISTString = () => new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const AdminCommonNumber = () => {
    const { marketKey } = useParams();
    const navigate = useNavigate();
    const meta = MARKET_META[marketKey];

    const [date, setDate] = useState(todayISTString());
    const [frDirect, setFrDirect] = useState("");
    const [frHouse, setFrHouse] = useState("");
    const [frEnding, setFrEnding] = useState("");
    const [srDirect, setSrDirect] = useState("");
    const [srHouse, setSrHouse] = useState("");
    const [srEnding, setSrEnding] = useState("");
    const [saving, setSaving] = useState(false);
    const [hydrated, setHydrated] = useState(false);

    const load = async () => {
        setHydrated(false);
        try {
            const { data } = await api.get("/predictions", {
                params: { type: "common_number", market_key: marketKey, date, limit: 1 },
            });
            const p = (data || [])[0];
            if (p) {
                setFrDirect((p.fr_direct || []).join(", "));
                setFrHouse(p.fr_house || "");
                setFrEnding(p.fr_ending || "");
                setSrDirect((p.sr_direct || []).join(", "));
                setSrHouse(p.sr_house || "");
                setSrEnding(p.sr_ending || "");
            } else {
                setFrDirect(""); setFrHouse(""); setFrEnding("");
                setSrDirect(""); setSrHouse(""); setSrEnding("");
            }
        } catch {} finally {
            setHydrated(true);
        }
    };

    useEffect(() => { load(); }, [date, marketKey]);

    const splitNums = (s) =>
        (s || "")
            .split(/[,\s]+/)
            .map((x) => x.trim())
            .filter(Boolean);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const fr_direct = splitNums(frDirect);
            const sr_direct = splitNums(srDirect);
            await api.post("/predictions", {
                type: "common_number",
                market_key: marketKey,
                date,
                numbers: [...fr_direct, ...sr_direct], // flat list for legacy display
                notes: "",
                fr_direct,
                fr_house: frHouse || null,
                fr_ending: frEnding || null,
                sr_direct,
                sr_house: srHouse || null,
                sr_ending: srEnding || null,
            });
            toast.success("Common Number saved");
            navigate("/admin");
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!meta) return <AdminShell title="Common Number"><div>Market not found</div></AdminShell>;

    const oneDigit = (v) => v.replace(/[^0-9]/g, "").slice(0, 1);

    return (
        <AdminShell title="Common Number">
            <div className="mb-3 rounded-md px-4 py-2 text-white text-sm font-semibold inline-flex items-center gap-2"
                 style={{ background: meta.accent, color: meta.headerTextColor || "#FFF" }}>
                <span className="text-lg">{meta.emoji}</span>
                {meta.name}
            </div>

            <h2 className="font-display font-bold text-xl md:text-2xl text-blue-950 tracking-tight uppercase">
                Update Common Number
            </h2>

            <form onSubmit={onSubmit} className={`mt-4 ${!hydrated ? "opacity-60 pointer-events-none" : ""}`} data-testid="admin-common-form">
                <label className="block text-slate-500 font-medium mb-2">Select Date:</label>
                <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="admin-common-date"
                    className="w-full border border-slate-300 rounded-md px-3 py-3 text-lg font-num focus:border-blue-800 outline-none"
                />

                <div className="mt-5 rounded-lg border border-slate-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-slate-500 font-semibold border-b border-slate-200">
                                <th className="px-3 py-3 text-left w-16"></th>
                                <th className="px-3 py-3 text-left">Direct</th>
                                <th className="px-3 py-3 text-left">House</th>
                                <th className="px-3 py-3 text-left">Ending</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-slate-100">
                                <td className="px-3 py-3 text-blue-900 font-semibold text-xs">F/R</td>
                                <td className="px-3 py-2">
                                    <input
                                        type="text"
                                        value={frDirect}
                                        onChange={(e) => setFrDirect(e.target.value)}
                                        placeholder="e.g. 75, 10"
                                        data-testid="admin-common-fr-direct"
                                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:border-blue-800 outline-none"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="text"
                                        value={frHouse}
                                        onChange={(e) => setFrHouse(oneDigit(e.target.value))}
                                        placeholder="0-9"
                                        data-testid="admin-common-fr-house"
                                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-base result-digit focus:border-blue-800 outline-none"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="text"
                                        value={frEnding}
                                        onChange={(e) => setFrEnding(oneDigit(e.target.value))}
                                        placeholder="0-9"
                                        data-testid="admin-common-fr-ending"
                                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-base result-digit focus:border-blue-800 outline-none"
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td className="px-3 py-3 text-blue-900 font-semibold text-xs">S/R</td>
                                <td className="px-3 py-2">
                                    <input
                                        type="text"
                                        value={srDirect}
                                        onChange={(e) => setSrDirect(e.target.value)}
                                        placeholder="e.g. 56"
                                        data-testid="admin-common-sr-direct"
                                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:border-blue-800 outline-none"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="text"
                                        value={srHouse}
                                        onChange={(e) => setSrHouse(oneDigit(e.target.value))}
                                        placeholder="0-9"
                                        data-testid="admin-common-sr-house"
                                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-base result-digit focus:border-blue-800 outline-none"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="text"
                                        value={srEnding}
                                        onChange={(e) => setSrEnding(oneDigit(e.target.value))}
                                        placeholder="0-9"
                                        data-testid="admin-common-sr-ending"
                                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-base result-digit focus:border-blue-800 outline-none"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="mt-6 flex justify-center">
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="admin-common-submit"
                        className="inline-flex items-center gap-2 px-8 py-2.5 rounded-md bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 disabled:opacity-60"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Submit
                    </button>
                </div>
            </form>
        </AdminShell>
    );
};

export default AdminCommonNumber;
